<script setup lang="ts">
import { computed } from 'vue';
import { get } from 'lodash';
import { useI18n } from 'vue-i18n';
import { useFlag } from '@loadsmart/vue-feature-flags';

import { YardTaskAttachmentAction, YardTaskCheckLocationOutcome, YardTaskEvent } from '@nova/core';

import { getFlagInfo } from './useYardTaskFlaggedEvent.helpers';
import { getAttachmentAction } from './useYardTaskAttachmentEvent.helpers';
import { useYardTaskTimeline } from '../../composables/useYardTaskTimeline';
import YardTaskAssignedEvent from './YardTaskAssignedEvent.vue';
import YardTaskAttachmentEvent from './YardTaskAttachmentEvent.vue';
import YardTaskCancelledEvent from './YardTaskCancelledEvent.vue';
import YardTaskCheckedEvent from './YardTaskCheckedEvent.vue';
import YardTaskCommentEvent from './YardTaskCommentEvent.vue';
import YardTaskCompletedEvent from './YardTaskCompletedEvent.vue';
import YardTaskCreatedEvent from './YardTaskCreatedEvent.vue';
import YardTaskFlaggedEvent from './YardTaskFlaggedEvent.vue';
import YardTaskGenericEvent from './YardTaskGenericEvent.vue';
import YardTaskBlockStateEvent from './YardTaskBlockStateEvent.vue';
import YardTaskInProgressEvent from './YardTaskInProgressEvent.vue';
import YardTaskSpotReserveStateEvent from './YardTaskSpotReserveStateEvent.vue';
import YardTaskUnassignedEvent from './YardTaskUnassignedEvent.vue';

import type { YardTaskTimelineEvent } from './types';

const MAP_EVENT_TYPE_TO_COMPONENT = {
  [YardTaskEvent.ASSIGNED]: YardTaskAssignedEvent,
  [YardTaskEvent.ATTACHMENTS_UPDATED]: YardTaskAttachmentEvent,
  [YardTaskEvent.BLOCKED]: YardTaskBlockStateEvent,
  [YardTaskEvent.CANCELLED]: YardTaskCancelledEvent,
  [YardTaskEvent.CHECKED]: YardTaskCheckedEvent,
  [YardTaskEvent.COMPLETED]: YardTaskCompletedEvent,
  [YardTaskEvent.CREATED]: YardTaskCreatedEvent,
  [YardTaskEvent.FLAGGED]: YardTaskFlaggedEvent,
  [YardTaskEvent.IN_PROGRESS]: YardTaskInProgressEvent,
  [YardTaskEvent.RESERVE_CANCELLED]: YardTaskSpotReserveStateEvent,
  [YardTaskEvent.SPOT_RESERVED]: YardTaskSpotReserveStateEvent,
  [YardTaskEvent.UNASSIGNED]: YardTaskUnassignedEvent,
  [YardTaskEvent.UNBLOCKED]: YardTaskBlockStateEvent
};

type EventTypeKey = keyof typeof MAP_EVENT_TYPE_TO_COMPONENT;

const isValidEventType = (eventType: unknown): eventType is EventTypeKey => {
  return typeof eventType === 'string' && eventType in MAP_EVENT_TYPE_TO_COMPONENT;
};

const MAP_EVENT_TYPE_TO_ICON: Record<EventTypeKey, string> = {
  [YardTaskEvent.ASSIGNED]: 'mdi-clipboard-edit-outline',
  [YardTaskEvent.ATTACHMENTS_UPDATED]: 'mdi-paperclip', // Default, overridden per action

  [YardTaskEvent.BLOCKED]: 'mdi-alert-circle-outline',
  [YardTaskEvent.CANCELLED]: 'mdi-close-circle',
  [YardTaskEvent.CHECKED]: 'mdi-clipboard-check-outline',
  [YardTaskEvent.COMPLETED]: 'mdi-check-circle',
  [YardTaskEvent.CREATED]: 'mdi-progress-helper',
  [YardTaskEvent.FLAGGED]: 'mdi-alert-circle-outline', // Default, may be overridden
  [YardTaskEvent.IN_PROGRESS]: 'mdi-progress-clock',
  [YardTaskEvent.RESERVE_CANCELLED]: 'mdi-lock-open-variant-outline',
  [YardTaskEvent.SPOT_RESERVED]: 'mdi-lock-outline',
  [YardTaskEvent.UNASSIGNED]: 'mdi-clipboard-edit-outline',
  [YardTaskEvent.UNBLOCKED]: 'mdi-check-circle-outline'
};

const MAP_FLAGGED_EVENT_TYPE_TO_ICON: Record<'issue' | 'urgent', string> = {
  issue: 'mdi-alert-circle-outline',
  urgent: 'mdi-flag'
};

type FlaggedIconType = keyof typeof MAP_FLAGGED_EVENT_TYPE_TO_ICON;

const isValidFlaggedIconType = (type: unknown): type is FlaggedIconType => {
  return typeof type === 'string' && type in MAP_FLAGGED_EVENT_TYPE_TO_ICON;
};

// TODO: (welington) extend Vuetify to use our colors.
const MAP_EVENT_TYPE_TO_ICON_COLOR: Record<EventTypeKey, string> = {
  [YardTaskEvent.ASSIGNED]: '#273038',
  [YardTaskEvent.ATTACHMENTS_UPDATED]: '#273038', // Default, overridden per action
  [YardTaskEvent.CANCELLED]: 'error',
  [YardTaskEvent.CHECKED]: 'success',
  [YardTaskEvent.COMPLETED]: 'success',
  [YardTaskEvent.CREATED]: '#585f66',
  [YardTaskEvent.FLAGGED]: 'warning', // Default, will be overridden by getEventIconColor
  [YardTaskEvent.IN_PROGRESS]: '#c96500',
  [YardTaskEvent.RESERVE_CANCELLED]: 'neutral-90',
  [YardTaskEvent.SPOT_RESERVED]: 'neutral-90',
  [YardTaskEvent.UNASSIGNED]: '#273038'
};

const MAP_FLAGGED_EVENT_TYPE_TO_ICON_COLOR: Record<'added' | 'removed', string> = {
  added: 'error',
  removed: '#838d96'
};

type FlaggedIconColorAction = keyof typeof MAP_FLAGGED_EVENT_TYPE_TO_ICON_COLOR;

const isValidFlaggedIconColorAction = (action: unknown): action is FlaggedIconColorAction => {
  return typeof action === 'string' && action in MAP_FLAGGED_EVENT_TYPE_TO_ICON_COLOR;
};

interface Props {
  taskId: string;
  warehouseTimezone: string;
}

const props = defineProps<Props>();

const { locale, t } = useI18n();

const { isLoading, groupedEvents } = useYardTaskTimeline({
  taskId: computed(() => props.taskId),
  warehouseTimezone: computed(() => props.warehouseTimezone)
});

const attachmentsFlag = useFlag('enable-yard-tasks-attachment-upload');
const areAttachmentsEnabled = computed(() => Boolean(attachmentsFlag.value?.enabled));

/** Attachment entries are held back with the rest of the attachments feature. */
const visibleGroups = computed(() => {
  if (areAttachmentsEnabled.value) {
    return groupedEvents.value;
  }

  return groupedEvents.value
    .map(([date, events]) => {
      const visible = events.filter(
        event => get(event, 'data.event') !== YardTaskEvent.ATTACHMENTS_UPDATED
      );

      return [date, visible] as (typeof groupedEvents.value)[number];
    })
    .filter(([, events]) => events.length > 0);
});

/**
 * Get the component for the event.
 * @param {YardTaskTimelineEvent} event - The event.
 * @returns {Component} The component for the event.
 */
const getEventComponent = (event: YardTaskTimelineEvent) => {
  const entity = get(event, 'entity');

  if (entity === 'Comment') {
    return YardTaskCommentEvent;
  }

  if (entity === 'YardTaskEvent') {
    const eventType = get(event, 'data.event');
    if (!eventType || !isValidEventType(eventType)) {
      return YardTaskGenericEvent;
    }

    return MAP_EVENT_TYPE_TO_COMPONENT[eventType] ?? YardTaskGenericEvent;
  }

  return YardTaskGenericEvent;
};

/**
 * Get the icon for the event.
 * @param {YardTaskTimelineEvent} event - The event.
 * @returns {string} The icon for the event.
 */
const getEventIcon = (event: YardTaskTimelineEvent) => {
  const entity = get(event, 'entity');

  if (entity === 'Comment') {
    return 'mdi-comment-text-outline';
  }

  if (entity === 'YardTaskEvent') {
    const eventType = get(event, 'data.event');

    // Special handling for flagged events
    if (eventType === YardTaskEvent.FLAGGED) {
      const flagInfo = getFlagInfo(event);

      if (flagInfo.type && isValidFlaggedIconType(flagInfo.type)) {
        return MAP_FLAGGED_EVENT_TYPE_TO_ICON[flagInfo.type] ?? 'mdi-flag';
      }

      return 'mdi-flag';
    }

    if (eventType === YardTaskEvent.CHECKED) {
      return get(event, 'data.data.outcome') === YardTaskCheckLocationOutcome.MISSING
        ? 'mdi-clipboard-remove-outline'
        : 'mdi-clipboard-check-outline';
    }

    if (eventType === YardTaskEvent.ATTACHMENTS_UPDATED) {
      return getAttachmentAction(event) === YardTaskAttachmentAction.REMOVED
        ? 'mdi-paperclip-off'
        : 'mdi-paperclip';
    }

    if (isValidEventType(eventType)) {
      return MAP_EVENT_TYPE_TO_ICON[eventType] ?? 'mdi-update';
    }

    return 'mdi-update';
  }

  return 'mdi-information-outline';
};

/**
 * Get the color for the event icon.
 * @param {YardTaskTimelineEvent} event - The event.
 * @returns {string} The color for the event icon.
 */
const getEventIconColor = (event: YardTaskTimelineEvent) => {
  const entity = get(event, 'entity');

  if (entity === 'Comment') {
    return '#273038';
  }

  if (entity === 'YardTaskEvent') {
    const eventType = get(event, 'data.event');

    // Special handling for flagged events
    if (eventType === YardTaskEvent.FLAGGED) {
      const flagInfo = getFlagInfo(event);

      if (flagInfo.action && isValidFlaggedIconColorAction(flagInfo.action)) {
        return MAP_FLAGGED_EVENT_TYPE_TO_ICON_COLOR[flagInfo.action] ?? 'primary';
      }

      return 'primary';
    }

    if (eventType === YardTaskEvent.CHECKED) {
      return get(event, 'data.data.outcome') === YardTaskCheckLocationOutcome.MISSING
        ? 'error'
        : 'success';
    }

    if (eventType === YardTaskEvent.ATTACHMENTS_UPDATED) {
      return getAttachmentAction(event) === YardTaskAttachmentAction.REMOVED
        ? '#838d96'
        : '#273038';
    }

    if (isValidEventType(eventType)) {
      return MAP_EVENT_TYPE_TO_ICON_COLOR[eventType] ?? 'neutral-90';
    }

    return 'neutral-90';
  }

  return 'primary';
};
</script>

<template>
  <div data-testid="yard-task-timeline">
    <div v-if="isLoading" class="d-flex justify-center align-center h-100 px-3 py-4">
      <v-progress-linear indeterminate color="primary"></v-progress-linear>
    </div>
    <div v-else-if="visibleGroups.length === 0" class="d-flex justify-center align-center pa-4">
      <span class="typography-body-sm text--color-text-tertiary">
        {{ t('global.timeline.noActivityYet') }}
      </span>
    </div>
    <div v-else class="d-flex flex-column">
      <template v-for="group in visibleGroups" :key="group[0]">
        <p class="typography-body-sm-bold text--color-text-tertiary px-0 py-3">{{ group[0] }}</p>

        <v-timeline
          class="yard-task-timeline"
          direction="vertical"
          align="center"
          side="end"
          line-inset="18">
          <v-timeline-item
            v-for="event in group[1]"
            :key="event.data.id"
            density="compact"
            dot-color="grey"
            size="28"
            data-testid="yard-task-timeline-event">
            <template v-slot:icon>
              <span
                style="
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  background-color: rgb(var(--v-theme-surface-light));
                ">
                <v-icon
                  :size="16"
                  :color="getEventIconColor(event)"
                  :icon="getEventIcon(event)"></v-icon>
              </span>
            </template>

            <div class="d-flex flex-column gap-1 pl-2 pt-1 pb-4">
              <component
                :is="getEventComponent(event)"
                :timezone="warehouseTimezone"
                :locale="locale"
                :event="event" />
            </div>
          </v-timeline-item>
        </v-timeline>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
.v-timeline.yard-task-timeline {
  --v-timeline-line-thickness: 1px;

  row-gap: $spacing-4 !important;
  justify-content: flex-start;

  .v-timeline-item__body {
    padding: 0 !important;
  }

  .v-card .v-card-title,
  .v-card .v-card-text {
    padding: 0 $spacing-2 !important;
  }
}
</style>
